// Modules
import { FormattedMessage } from "react-intl";
// Material-UI
import { Button, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle } from "@mui/material";

type ConfirmationDialogAction = {
    label: string; // i18n key or text
    action: () => void;
    color?: "primary" | "error" | "info" | "secondary";
    variant?: "text" | "outlined" | "contained";
    autoFocus?: boolean;
};

type ConfirmationDialogProps = {
    open: boolean;
    title?: React.ReactNode;
    message?: React.ReactNode;
    messageId?: string;
    messageValues?: Record<string, any>;
    details?: React.ReactNode[];
    actions: ConfirmationDialogAction[];
    handleClose: () => void;
    alignButtons?: "left" | "center" | "right";
};

export default function ConfirmationDialog({
    open,
    title,
    message,
    messageId,
    messageValues,
    details,
    actions,
    handleClose,
    alignButtons = "right"
}: ConfirmationDialogProps) {
    return (
        <Dialog open={open} onClose={handleClose} maxWidth="xs" fullWidth>
            {title && <DialogTitle>{title}</DialogTitle>}

            {message ? (
                <DialogContent>
                    <DialogContentText>{message}</DialogContentText>
                </DialogContent>
            ) : messageId ? (
                <DialogContent>
                    <DialogContentText>
                        <FormattedMessage id={messageId} values={messageValues} />
                    </DialogContentText>
                </DialogContent>
            ) : null}

            {details && details.length > 0 && (
                <DialogContent>
                    {details.map((item, index) => (
                        <DialogContentText key={index} sx={{ mt: index > 0 ? 1 : 0 }}>
                            {item}
                        </DialogContentText>
                    ))}
                </DialogContent>
            )}

            <DialogActions
                sx={{
                    justifyContent:
                        alignButtons === "left" ? "flex-start" : alignButtons === "center" ? "center" : "flex-end",
                    mr: 2,
                    gap: 1
                }}
            >
                {actions?.map((btn, idx) => (
                    <Button
                        key={idx}
                        onClick={btn.action}
                        color={btn.color || "primary"}
                        variant={btn.variant || "text"}
                        autoFocus={btn.autoFocus}
                        data-testid={`confirm-${btn.label}-button`}
                    >
                        <FormattedMessage id={btn.label} />
                    </Button>
                ))}
            </DialogActions>
        </Dialog>
    );
}
